import { Content, Header, Page } from '@backstage/core-components';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Pagination,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AppsIcon from '@mui/icons-material/Apps';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LinkIcon from '@mui/icons-material/Link';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import SyncIcon from '@mui/icons-material/Sync';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { AppSetsView } from './AppSetsView';
import { type ArgoCdApi, useArgoCdApi } from './api';
import { ResourceDialog } from './ResourceDialog';
import { SyncPanel } from './SyncPanel';
import type {
  ArgoApplication,
  ArgoApplicationDetails,
  ArgoApplicationsResponse,
  ArgoResource,
  ArgoResourceDetails,
  ArgoRevisionMetadata,
  ArgoSyncRequest,
} from './types';
import {
  COLORS,
  Field,
  healthColor,
  KeyValueChips,
  Metric,
  safeExternalUrl,
  StatusChip,
  syncColor,
} from './ui';
import { useUrlState } from '../shared/useUrlState';
import {
  ARGO_NAVIGATION_KEYS,
  appKey as urlAppKey,
  parseAppKey as parseUrlAppKey,
  parseArgoUrl,
  resourceKey as urlResourceKey,
  serializeArgoUrl,
  type ArgoAppTab,
  type ArgoResourceTab,
  type ArgoTab,
  type ArgoUrlState,
  type ArgoView,
} from './argocdUrl';

const PAGE_SIZE = 24;
const FAVORITES_KEY = 'argocd-favorites';
const VIEW_KEY = 'argocd-view';

const appKey = (application: ArgoApplication) =>
  `${application.instance}/${application.name}`;

function loadFavorites(): Set<string> {
  try {
    const raw = window.localStorage.getItem(FAVORITES_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

function ApplicationCard({
  application,
  busy,
  favorite,
  onToggleFavorite,
  onOpen,
  onRefresh,
  onSync,
}: {
  application: ArgoApplication;
  busy: boolean;
  favorite: boolean;
  onToggleFavorite: () => void;
  onOpen: () => void;
  onRefresh: () => void;
  onSync: () => void;
}) {
  return (
    <Card
      onClick={onOpen}
      sx={{
        position: 'relative',
        border: `1px solid ${COLORS.border}`,
        borderLeft: `4px solid ${healthColor(application.healthStatus)}`,
        borderRadius: 2,
        boxShadow: '0 1px 3px rgba(9,30,66,0.13)',
        cursor: 'pointer',
        transition: 'box-shadow 120ms, transform 120ms',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(9,30,66,0.18)',
          transform: 'translateY(-1px)',
        },
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <AppsIcon sx={{ mt: 0.2, color: COLORS.muted, fontSize: 20 }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              title={application.name}
              sx={{
                color: COLORS.text,
                fontSize: 14,
                fontWeight: 700,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {application.name}
            </Typography>
            <Typography sx={{ color: COLORS.muted, fontSize: 11 }}>
              {application.instance} · {application.project}
            </Typography>
          </Box>
          <IconButton
            size="small"
            aria-label="Toggle favorite"
            onClick={event => {
              event.stopPropagation();
              onToggleFavorite();
            }}
          >
            {favorite ? (
              <StarIcon sx={{ color: COLORS.amber, fontSize: 17 }} />
            ) : (
              <StarBorderIcon sx={{ fontSize: 17 }} />
            )}
          </IconButton>
          <Tooltip title="Open in Argo CD">
            <IconButton
              size="small"
              component="a"
              href={application.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={event => event.stopPropagation()}
            >
              <OpenInNewIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>
        </Box>

        <Stack
          direction="row"
          spacing={0.75}
          sx={{ my: 1.5, flexWrap: 'wrap', gap: 0.5 }}
        >
          <StatusChip
            label={application.healthStatus}
            color={healthColor(application.healthStatus)}
          />
          <StatusChip
            label={application.syncStatus}
            color={syncColor(application.syncStatus)}
          />
          {application.automated && (
            <StatusChip label="Auto-sync" color={COLORS.purple} />
          )}
        </Stack>

        <Field label="Repository" value={application.repoUrl} />
        <Field label="Revision" value={application.targetRevision} />
        <Field label="Path" value={application.path} />
        <Field
          label="Destination"
          value={application.destinationName ?? application.destinationServer}
        />
        <Field label="Namespace" value={application.destinationNamespace} />
        <Field
          label="Created"
          value={
            application.createdAt
              ? new Date(application.createdAt).toLocaleString()
              : undefined
          }
        />
        <Field
          label="Last sync"
          value={
            application.lastSyncAt
              ? new Date(application.lastSyncAt).toLocaleString()
              : undefined
          }
        />

        <Divider sx={{ my: 1.25 }} />
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="contained"
            startIcon={
              busy ? (
                <CircularProgress color="inherit" size={13} />
              ) : (
                <SyncIcon />
              )
            }
            disabled={busy}
            onClick={event => {
              event.stopPropagation();
              onSync();
            }}
            sx={{ textTransform: 'none', bgcolor: COLORS.blue }}
          >
            Sync
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            disabled={busy}
            onClick={event => {
              event.stopPropagation();
              onRefresh();
            }}
            sx={{ textTransform: 'none' }}
          >
            Refresh
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

function ApplicationsTable({
  applications,
  busyKey,
  favorites,
  onToggleFavorite,
  onOpen,
  onRefresh,
  onSync,
}: {
  applications: ArgoApplication[];
  busyKey: string | null;
  favorites: Set<string>;
  onToggleFavorite: (application: ArgoApplication) => void;
  onOpen: (application: ArgoApplication) => void;
  onRefresh: (application: ArgoApplication) => void;
  onSync: (application: ArgoApplication) => void;
}) {
  return (
    <TableContainer
      sx={{ border: `1px solid ${COLORS.border}`, borderRadius: 2 }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 34 }} />
            <TableCell>Application</TableCell>
            <TableCell>Health</TableCell>
            <TableCell>Sync</TableCell>
            <TableCell>Revision</TableCell>
            <TableCell>Destination</TableCell>
            <TableCell>Last sync</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {applications.map(application => {
            const key = appKey(application);
            const busy = busyKey === key;
            return (
              <TableRow
                hover
                key={`${key}/${application.appNamespace ?? ''}`}
                onClick={() => onOpen(application)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={event => {
                      event.stopPropagation();
                      onToggleFavorite(application);
                    }}
                  >
                    {favorites.has(key) ? (
                      <StarIcon sx={{ color: COLORS.amber, fontSize: 16 }} />
                    ) : (
                      <StarBorderIcon sx={{ fontSize: 16 }} />
                    )}
                  </IconButton>
                </TableCell>
                <TableCell>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
                    {application.name}
                  </Typography>
                  <Typography sx={{ color: COLORS.muted, fontSize: 10.5 }}>
                    {application.instance} · {application.project}
                    {application.automated ? ' · auto-sync' : ''}
                  </Typography>
                </TableCell>
                <TableCell>
                  <StatusChip
                    label={application.healthStatus}
                    color={healthColor(application.healthStatus)}
                  />
                </TableCell>
                <TableCell>
                  <StatusChip
                    label={application.syncStatus}
                    color={syncColor(application.syncStatus)}
                  />
                </TableCell>
                <TableCell sx={{ maxWidth: 140 }}>
                  <Typography
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontFamily: 'monospace',
                      fontSize: 11,
                    }}
                  >
                    {application.targetRevision ?? '—'}
                  </Typography>
                </TableCell>
                <TableCell sx={{ fontSize: 12 }}>
                  {application.destinationName ??
                    application.destinationServer ??
                    '—'}
                  <Typography sx={{ color: COLORS.muted, fontSize: 10.5 }}>
                    {application.destinationNamespace ?? '—'}
                  </Typography>
                </TableCell>
                <TableCell sx={{ fontSize: 12 }}>
                  {application.lastSyncAt
                    ? new Date(application.lastSyncAt).toLocaleString()
                    : '—'}
                </TableCell>
                <TableCell align="right" onClick={event => event.stopPropagation()}>
                  <Tooltip title="Sync">
                    <span>
                      <IconButton
                        size="small"
                        disabled={busy}
                        onClick={() => onSync(application)}
                      >
                        {busy ? (
                          <CircularProgress size={14} />
                        ) : (
                          <SyncIcon sx={{ fontSize: 17 }} />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Refresh">
                    <span>
                      <IconButton
                        size="small"
                        disabled={busy}
                        onClick={() => onRefresh(application)}
                      >
                        <RefreshIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Open in Argo CD">
                    <IconButton
                      size="small"
                      component="a"
                      href={application.externalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <OpenInNewIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function HealthBar({
  applications,
  onSelect,
}: {
  applications: ArgoApplication[];
  onSelect: (status: string) => void;
}) {
  const segments = useMemo(() => {
    const counts = new Map<string, number>();
    applications.forEach(application =>
      counts.set(
        application.healthStatus,
        (counts.get(application.healthStatus) ?? 0) + 1,
      ),
    );
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [applications]);

  if (!applications.length) return null;
  return (
    <Box
      sx={{
        display: 'flex',
        mb: 2,
        height: 10,
        borderRadius: 5,
        overflow: 'hidden',
        border: `1px solid ${COLORS.border}`,
      }}
    >
      {segments.map(([status, count]) => (
        <Tooltip key={status} title={`${status}: ${count}`}>
          <Box
            onClick={() => onSelect(status)}
            sx={{
              flex: count,
              bgcolor: healthColor(status),
              cursor: 'pointer',
              '&:hover': { opacity: 0.8 },
            }}
          />
        </Tooltip>
      ))}
    </Box>
  );
}

function resourceState(resource: ArgoResource): {
  label: string;
  color: string;
} {
  if (resource.healthStatus === 'Healthy' || resource.status === 'Synced') {
    return {
      label: resource.healthStatus ?? resource.status ?? 'Healthy',
      color: COLORS.green,
    };
  }
  if (resource.healthStatus === 'Degraded' || resource.status === 'OutOfSync') {
    return {
      label: resource.healthStatus ?? resource.status ?? 'Degraded',
      color: COLORS.red,
    };
  }
  return {
    label: resource.healthStatus ?? resource.status ?? 'Unknown',
    color: COLORS.muted,
  };
}

function resourceKey(resource: {
  uid?: string;
  group?: string;
  kind?: string;
  namespace?: string;
  name?: string;
}) {
  return (
    resource.uid ??
    [
      resource.group ?? '',
      resource.kind ?? '',
      resource.namespace ?? '',
      resource.name ?? '',
    ].join('/')
  );
}

function orderResources(resources: ArgoResource[]) {
  const resourcesByKey = new Map(
    resources.map(resource => [resourceKey(resource), resource]),
  );
  const children = new Map<string, ArgoResource[]>();
  const roots: ArgoResource[] = [];

  for (const resource of resources) {
    const parent = resource.parentRefs
      .map(parentRef => resourcesByKey.get(resourceKey(parentRef)))
      .find(Boolean);
    if (!parent) {
      roots.push(resource);
      continue;
    }
    const parentKey = resourceKey(parent);
    children.set(parentKey, [...(children.get(parentKey) ?? []), resource]);
  }

  const ordered: Array<{ resource: ArgoResource; depth: number }> = [];
  const visited = new Set<string>();
  const visit = (resource: ArgoResource, depth: number) => {
    const key = resourceKey(resource);
    if (visited.has(key)) return;
    visited.add(key);
    ordered.push({ resource, depth });
    for (const child of children.get(key) ?? []) visit(child, depth + 1);
  };
  roots.forEach(resource => visit(resource, 0));
  resources.forEach(resource => visit(resource, 0));
  return ordered;
}

function RevisionMeta({ metadata }: { metadata: ArgoRevisionMetadata }) {
  return (
    <>
      <Field label="Author" value={metadata.author} />
      <Field
        label="Authored"
        value={
          metadata.date ? new Date(metadata.date).toLocaleString() : undefined
        }
      />
      <Field label="Message" value={metadata.message?.trim()} />
    </>
  );
}

function ApplicationDrawer({
  api,
  application,
  details,
  loading,
  busy,
  onClose,
  onRefresh,
  onSync,
  onResource,
  onRollback,
  onTerminate,
  tab,
  onTabChange,
}: {
  api: ArgoCdApi;
  application: ArgoApplication | null;
  details: ArgoApplicationDetails | null;
  loading: boolean;
  busy: boolean;
  onClose: () => void;
  onRefresh: (type: 'normal' | 'hard') => void;
  onSync: () => void;
  onResource: (resource: ArgoResource) => void;
  onRollback: (entry: { id?: number; revision?: string }) => void;
  onTerminate: () => void;
  /** Owned by the page so the open tab is part of the URL. */
  tab: ArgoAppTab;
  onTabChange: (tab: ArgoAppTab) => void;
}) {
  const [refreshAnchor, setRefreshAnchor] = useState<HTMLElement | null>(null);
  const [revisionMeta, setRevisionMeta] = useState<ArgoRevisionMetadata | null>(
    null,
  );
  const [historyMeta, setHistoryMeta] = useState<
    Record<string, ArgoRevisionMetadata | null>
  >({});
  const [expandedHistory, setExpandedHistory] = useState<Set<string>>(
    new Set(),
  );
  const orderedResources = orderResources(details?.resources ?? []);
  const syncBlockedReason =
    details && !details.access.rbacCanSync
      ? 'Your Argo CD RBAC policy does not allow this application to sync.'
      : details && !details.access.syncWindowCanSync
      ? 'The active Argo CD sync window blocks manual synchronization.'
      : null;
  const operationRunning =
    details?.operation &&
    (details.operation.phase === 'Running' ||
      details.operation.phase === 'Terminating' ||
      !details.operation.finishedAt);
  const canRollback = Boolean(details && !details.syncPolicy.automated);

  useEffect(() => {
    setHistoryMeta({});
    setExpandedHistory(new Set());
  }, [application]);

  useEffect(() => {
    setRevisionMeta(null);
    if (!application || !details?.revision) return undefined;
    let cancelled = false;
    api
      .revisionMetadata(application, details.revision)
      .then(metadata => {
        if (!cancelled) setRevisionMeta(metadata);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [api, application, details?.revision]);

  const toggleHistory = async (entry: {
    id?: number;
    revision?: string;
    revisions?: string[];
  }) => {
    const revision = entry.revision ?? entry.revisions?.[0];
    if (!revision || !application) return;
    setExpandedHistory(current => {
      const next = new Set(current);
      if (next.has(revision)) next.delete(revision);
      else next.add(revision);
      return next;
    });
    if (!(revision in historyMeta)) {
      try {
        const metadata = await api.revisionMetadata(application, revision);
        setHistoryMeta(current => ({ ...current, [revision]: metadata }));
      } catch {
        setHistoryMeta(current => ({ ...current, [revision]: null }));
      }
    }
  };

  return (
    <Drawer
      anchor="right"
      open={Boolean(application)}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', md: 920 },
          maxWidth: '100vw',
          top: 'var(--oc-masthead-height, 0px)',
          height: 'calc(100% - var(--oc-masthead-height, 0px))',
        },
      }}
    >
      {application && (
        <Box sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{ color: COLORS.text, fontSize: 20, fontWeight: 700 }}
              >
                {application.name}
              </Typography>
              <Typography sx={{ color: COLORS.muted, fontSize: 12 }}>
                {application.instance} · {application.project}
              </Typography>
            </Box>
            <IconButton onClick={onClose} aria-label="Close details">
              <CloseIcon />
            </IconButton>
          </Box>

          <Stack direction="row" spacing={1} sx={{ my: 2 }}>
            <Button
              variant="contained"
              startIcon={
                busy ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <SyncIcon />
                )
              }
              disabled={busy || loading || Boolean(syncBlockedReason)}
              onClick={onSync}
              sx={{ textTransform: 'none', bgcolor: COLORS.blue }}
            >
              Sync
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              endIcon={<ArrowDropDownIcon />}
              disabled={busy}
              onClick={event => setRefreshAnchor(event.currentTarget)}
              sx={{ textTransform: 'none' }}
            >
              Refresh
            </Button>
            <Menu
              anchorEl={refreshAnchor}
              open={Boolean(refreshAnchor)}
              onClose={() => setRefreshAnchor(null)}
            >
              <MenuItem
                onClick={() => {
                  setRefreshAnchor(null);
                  onRefresh('normal');
                }}
              >
                Refresh
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setRefreshAnchor(null);
                  onRefresh('hard');
                }}
              >
                Hard refresh (invalidate cache)
              </MenuItem>
            </Menu>
            {operationRunning && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<StopCircleOutlinedIcon />}
                disabled={busy}
                onClick={onTerminate}
                sx={{ textTransform: 'none' }}
              >
                Terminate
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              href={application.externalUrl}
              target="_blank"
              sx={{ textTransform: 'none' }}
            >
              Argo CD
            </Button>
          </Stack>

          <Stack direction="row" spacing={0.75} sx={{ mb: 2 }}>
            <StatusChip
              label={application.healthStatus}
              color={healthColor(application.healthStatus)}
            />
            <StatusChip
              label={application.syncStatus}
              color={syncColor(application.syncStatus)}
            />
            {details?.operation?.phase && (
              <StatusChip
                label={details.operation.phase}
                color={
                  details.operation.phase === 'Succeeded'
                    ? COLORS.green
                    : details.operation.phase === 'Failed' ||
                      details.operation.phase === 'Error'
                    ? COLORS.red
                    : COLORS.amber
                }
              />
            )}
          </Stack>

          <Tabs
            value={tab}
            onChange={(_event, value) => onTabChange(value as ArgoAppTab)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 2, borderBottom: `1px solid ${COLORS.border}` }}
          >
            <Tab label="Summary" value="summary" />
            <Tab
              label={`Resources (${details?.resources.length ?? 0})`}
              value="resources"
            />
            <Tab label="Operation" value="operation" />
            <Tab
              label={`History (${details?.history.length ?? 0})`}
              value="history"
            />
          </Tabs>

          {(!details || tab === 'summary') && (
            <Box
              sx={{
                p: 1.5,
                border: `1px solid ${COLORS.border}`,
                borderRadius: 2,
              }}
            >
              <Field label="Project" value={application.project} />
              <Field label="Repository" value={application.repoUrl} />
              <Field
                label="Target revision"
                value={application.targetRevision}
              />
              <Field label="Path" value={application.path} />
              <Field
                label="Destination"
                value={
                  application.destinationName ?? application.destinationServer
                }
              />
              <Field
                label="Namespace"
                value={application.destinationNamespace}
              />
              <Field label="Current revision" value={application.revision} />
              {revisionMeta && <RevisionMeta metadata={revisionMeta} />}
              <Field
                label="Last reconciled"
                value={
                  application.reconciledAt
                    ? new Date(application.reconciledAt).toLocaleString()
                    : undefined
                }
              />
              <Field
                label="Created"
                value={
                  application.createdAt
                    ? new Date(application.createdAt).toLocaleString()
                    : undefined
                }
              />
              <Field
                label="Last sync"
                value={
                  application.lastSyncAt
                    ? new Date(application.lastSyncAt).toLocaleString()
                    : undefined
                }
              />
              {details && (
                <>
                  <Field
                    label="Revision history"
                    value={details.revisionHistoryLimit}
                  />
                  <Field
                    label="Sync policy"
                    value={
                      details.syncPolicy.automated
                        ? `Automated · prune ${
                            details.syncPolicy.automated.prune ? 'on' : 'off'
                          } · self-heal ${
                            details.syncPolicy.automated.selfHeal ? 'on' : 'off'
                          }`
                        : 'Manual'
                    }
                  />
                  <Field
                    label="Sync options"
                    value={
                      details.syncPolicy.syncOptions.length
                        ? details.syncPolicy.syncOptions.join(', ')
                        : undefined
                    }
                  />
                </>
              )}
            </Box>
          )}

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {details && tab === 'summary' && details.sources.length > 1 && (
            <Box sx={{ mt: 2 }}>
              <Typography
                sx={{
                  mb: 1,
                  color: COLORS.text,
                  fontSize: 14,
                  fontWeight: 700,
                }}
              >
                Sources
              </Typography>
              {details.sources.map((source, index) => (
                <Box
                  key={`${source.repoUrl}-${source.ref}-${index}`}
                  sx={{
                    mb: 1,
                    p: 1.5,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 2,
                  }}
                >
                  <Field label={`Source ${index + 1}`} value={source.repoUrl} />
                  <Field label="Revision" value={source.targetRevision} />
                  <Field
                    label="Path / chart"
                    value={source.path ?? source.chart}
                  />
                  <Field label="Reference" value={source.ref} />
                </Box>
              ))}
            </Box>
          )}

          {details && tab === 'summary' && (
            <Box sx={{ mt: 2 }}>
              <Typography
                sx={{
                  mb: 1,
                  color: COLORS.text,
                  fontSize: 14,
                  fontWeight: 700,
                }}
              >
                Metadata
              </Typography>
              <Typography
                sx={{
                  mb: 0.75,
                  color: COLORS.muted,
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                LABELS
              </Typography>
              <KeyValueChips values={details.labels} />
              <Typography
                sx={{
                  mt: 1.5,
                  mb: 0.75,
                  color: COLORS.muted,
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                ANNOTATIONS
              </Typography>
              <KeyValueChips values={details.annotations} />
              {details.summary.externalUrls.length > 0 && (
                <>
                  <Typography
                    sx={{
                      mt: 1.5,
                      mb: 0.75,
                      color: COLORS.muted,
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    EXTERNAL URLS
                  </Typography>
                  <Stack spacing={0.5}>
                    {details.summary.externalUrls.map(
                      url =>
                        safeExternalUrl(url) && (
                          <Typography
                            key={url}
                            component="a"
                            href={safeExternalUrl(url) ?? undefined}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ color: COLORS.blue, fontSize: 12 }}
                          >
                            {url.includes('|') ? url.split('|')[0] : url}
                          </Typography>
                        ),
                    )}
                  </Stack>
                </>
              )}
              {details.summary.images.length > 0 && (
                <>
                  <Typography
                    sx={{
                      mt: 1.5,
                      mb: 0.75,
                      color: COLORS.muted,
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    IMAGES
                  </Typography>
                  <Stack spacing={0.5}>
                    {details.summary.images.map(image => (
                      <Typography
                        key={image}
                        sx={{ fontFamily: 'monospace', fontSize: 11 }}
                      >
                        {image}
                      </Typography>
                    ))}
                  </Stack>
                </>
              )}
              {details.info.map(item => (
                <Field
                  key={item.name}
                  label={item.name ?? 'Info'}
                  value={item.value}
                />
              ))}
            </Box>
          )}

          {details?.operation && tab === 'operation' && (
            <Box sx={{ mt: 2 }}>
              <Typography
                sx={{
                  mb: 1,
                  color: COLORS.text,
                  fontSize: 14,
                  fontWeight: 700,
                }}
              >
                Latest operation
              </Typography>
              <Alert
                severity={
                  details.operation.phase === 'Succeeded'
                    ? 'success'
                    : details.operation.phase === 'Failed' ||
                      details.operation.phase === 'Error'
                    ? 'error'
                    : 'info'
                }
              >
                <strong>{details.operation.phase ?? 'Unknown'}</strong>
                {details.operation.message
                  ? ` — ${details.operation.message}`
                  : ''}
              </Alert>
              <Box sx={{ mt: 1.5 }}>
                <Field label="Revision" value={details.operation.revision} />
                <Field
                  label="Initiated by"
                  value={
                    details.operation.initiatedBy?.username ??
                    (details.operation.initiatedBy?.automated
                      ? 'Automated'
                      : undefined)
                  }
                />
                <Field
                  label="Started"
                  value={
                    details.operation.startedAt
                      ? new Date(details.operation.startedAt).toLocaleString()
                      : undefined
                  }
                />
                <Field
                  label="Finished"
                  value={
                    details.operation.finishedAt
                      ? new Date(details.operation.finishedAt).toLocaleString()
                      : undefined
                  }
                />
              </Box>
              <TableContainer
                sx={{
                  mt: 1.5,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 2,
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Resource</TableCell>
                      <TableCell>Phase</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Message</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {details.operation.resources.map((resource, index) => (
                      <TableRow
                        key={`${resource.kind}-${resource.namespace}-${resource.name}-${index}`}
                      >
                        <TableCell sx={{ fontSize: 12 }}>
                          <strong>{resource.name}</strong>
                          <br />
                          <Typography
                            component="span"
                            sx={{ color: COLORS.muted, fontSize: 10.5 }}
                          >
                            {resource.kind} ·{' '}
                            {resource.namespace ?? 'cluster-scoped'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>
                          {resource.syncPhase ?? resource.hookPhase ?? '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>
                          {resource.status ?? '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>
                          {resource.message ?? '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
          {details && !details.operation && tab === 'operation' && (
            <Alert severity="info">
              No operation has been recorded for this application.
            </Alert>
          )}

          {tab === 'summary' && syncBlockedReason && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {syncBlockedReason}
            </Alert>
          )}

          {details &&
            tab === 'summary' &&
            details.access.assignedWindows.length > 0 && (
              <Alert
                severity={details.access.syncWindowCanSync ? 'info' : 'warning'}
                sx={{ mt: 2 }}
              >
                {details.access.syncWindowCanSync
                  ? 'Manual sync is allowed by the assigned sync window.'
                  : 'Manual sync is currently blocked by the assigned sync window.'}
              </Alert>
            )}

          {tab === 'summary' &&
            details?.conditions.map((condition, index) => (
              <Alert
                key={`${condition.type}-${index}`}
                severity="warning"
                sx={{ mt: 1 }}
              >
                <strong>{condition.type}</strong>
                {condition.message ? ` — ${condition.message}` : ''}
              </Alert>
            ))}

          {details && tab === 'resources' && (
            <Box sx={{ mt: 2 }}>
              <Typography
                sx={{
                  mb: 1,
                  color: COLORS.text,
                  fontSize: 14,
                  fontWeight: 700,
                }}
              >
                Resources ({details.resources.length})
              </Typography>
              <TableContainer
                sx={{ border: `1px solid ${COLORS.border}`, borderRadius: 2 }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Resource</TableCell>
                      <TableCell>Namespace</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orderedResources.map(({ resource, depth }, index) => {
                      const state = resourceState(resource);
                      return (
                        <TableRow
                          hover
                          key={`${resource.kind}-${resource.namespace}-${resource.name}-${index}`}
                          onClick={() => onResource(resource)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                pl: Math.min(depth, 6) * 2,
                              }}
                            >
                              {depth > 0 && (
                                <Typography
                                  component="span"
                                  sx={{
                                    mr: 0.75,
                                    color: COLORS.border,
                                    fontSize: 14,
                                  }}
                                >
                                  └
                                </Typography>
                              )}
                              <Typography
                                sx={{ fontSize: 12.5, fontWeight: 600 }}
                              >
                                {resource.name}
                              </Typography>
                            </Box>
                            <Typography
                              sx={{ color: COLORS.muted, fontSize: 10.5 }}
                            >
                              {resource.group ? `${resource.group}/` : ''}
                              {resource.kind}
                              {resource.orphaned ? ' · Orphaned' : ''}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontSize: 12 }}>
                            {resource.namespace ?? '—'}
                          </TableCell>
                          <TableCell>
                            <StatusChip
                              label={state.label}
                              color={state.color}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {details && tab === 'history' && (
            <Box sx={{ mt: 2 }}>
              <Typography
                sx={{
                  mb: 1,
                  color: COLORS.text,
                  fontSize: 14,
                  fontWeight: 700,
                }}
              >
                Deployment history
              </Typography>
              {!canRollback && details.history.length > 0 && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  Auto-sync is enabled — disable it in Argo CD before rolling
                  back.
                </Alert>
              )}
              <TableContainer
                sx={{ border: `1px solid ${COLORS.border}`, borderRadius: 2 }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Revision</TableCell>
                      <TableCell>Initiated by</TableCell>
                      <TableCell>Deployed</TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {details.history.map((entry, index) => {
                      const revision = entry.revision ?? entry.revisions?.[0];
                      const expanded = revision
                        ? expandedHistory.has(revision)
                        : false;
                      const metadata = revision
                        ? historyMeta[revision]
                        : undefined;
                      return (
                        <React.Fragment
                          key={`${entry.id ?? 'history'}-${index}`}
                        >
                          <TableRow>
                            <TableCell sx={{ fontSize: 12 }}>
                              {entry.id ?? '—'}
                            </TableCell>
                            <TableCell sx={{ maxWidth: 250, fontSize: 12 }}>
                              <Tooltip
                                title={
                                  entry.revisions?.join(', ') ??
                                  entry.revision ??
                                  ''
                                }
                              >
                                <Typography
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    fontFamily: 'monospace',
                                    fontSize: 11,
                                  }}
                                >
                                  {entry.revisions?.join(', ') ??
                                    entry.revision ??
                                    '—'}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={{ fontSize: 12 }}>
                              {entry.initiatedBy?.username ??
                                (entry.initiatedBy?.automated
                                  ? 'Automated'
                                  : '—')}
                            </TableCell>
                            <TableCell sx={{ fontSize: 12 }}>
                              {entry.deployedAt
                                ? new Date(entry.deployedAt).toLocaleString()
                                : '—'}
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                              {revision && (
                                <Tooltip title="Revision details">
                                  <IconButton
                                    size="small"
                                    onClick={() => toggleHistory(entry)}
                                  >
                                    <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {entry.id !== undefined && (
                                <Tooltip
                                  title={
                                    canRollback
                                      ? 'Roll back to this revision'
                                      : 'Disable auto-sync to roll back'
                                  }
                                >
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={!canRollback || busy}
                                      onClick={() => onRollback(entry)}
                                    >
                                      <ReplayIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                          {expanded && (
                            <TableRow>
                              <TableCell colSpan={5} sx={{ bgcolor: COLORS.surface }}>
                                {metadata === undefined ? (
                                  <CircularProgress size={14} />
                                ) : metadata === null ? (
                                  <Typography
                                    sx={{ color: COLORS.muted, fontSize: 12 }}
                                  >
                                    No revision metadata available
                                  </Typography>
                                ) : (
                                  <RevisionMeta metadata={metadata} />
                                )}
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {details.history.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          No deployment history
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      )}
    </Drawer>
  );
}

export function ArgoCdPage() {
  const api = useArgoCdApi();
  // Read the address once; everything below is written back to it, so any
  // filtered list, open application, resource or tab can be linked to.
  const initialUrl = useRef(
    parseArgoUrl(new URLSearchParams(window.location.search)),
  ).current;

  const [pageTab, setPageTab] = useState<ArgoTab>(initialUrl.tab);
  const [data, setData] = useState<ArgoApplicationsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState(initialUrl.q);
  const [instanceFilter, setInstanceFilter] = useState(initialUrl.instance);
  const [syncFilter, setSyncFilter] = useState(initialUrl.sync);
  const [healthFilter, setHealthFilter] = useState(initialUrl.health);
  const [projectFilter, setProjectFilter] = useState(initialUrl.project);
  const [clusterFilter, setClusterFilter] = useState(initialUrl.cluster);
  const [namespaceFilter, setNamespaceFilter] = useState(initialUrl.ns);
  const [autoSyncFilter, setAutoSyncFilter] = useState(initialUrl.autosync);
  const [labelFilter, setLabelFilter] = useState<string[]>(initialUrl.labels);
  const [favoritesOnly, setFavoritesOnly] = useState(initialUrl.favoritesOnly);
  const [appTab, setAppTab] = useState<ArgoAppTab>(initialUrl.appTab);
  const [resTab, setResTab] = useState<ArgoResourceTab>(initialUrl.resTab);
  // Keys from a link that has not been resolved against loaded data yet.
  const [pendingApp, setPendingApp] = useState(initialUrl.app);
  const [pendingRes, setPendingRes] = useState(initialUrl.res);
  const [favorites, setFavorites] = useState<Set<string>>(loadFavorites);
  // A link naming a view mode wins over the stored preference, the way
  // upstream seeds its persisted preferences from query params.
  const [view, setView] = useState<ArgoView>(
    () =>
      initialUrl.view ||
      (window.localStorage.getItem(VIEW_KEY) as ArgoView) ||
      'tiles',
  );
  const [page, setPage] = useState(initialUrl.page);
  const [selected, setSelected] = useState<ArgoApplication | null>(null);
  const [details, setDetails] = useState<ArgoApplicationDetails | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selectedResource, setSelectedResource] = useState<ArgoResource | null>(
    null,
  );
  const [resourceDetails, setResourceDetails] =
    useState<ArgoResourceDetails | null>(null);
  const [resourceLoading, setResourceLoading] = useState(false);
  const [resourceError, setResourceError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [syncPanel, setSyncPanel] = useState<{
    application: ArgoApplication;
    resources: ArgoResource[];
  } | null>(null);
  const [rollbackCandidate, setRollbackCandidate] = useState<{
    application: ArgoApplication;
    id: number;
    revision?: string;
  } | null>(null);
  const [rollbackPrune, setRollbackPrune] = useState(false);
  const [terminateOpen, setTerminateOpen] = useState(false);
  const [notification, setNotification] = useState<{
    message: string;
    severity: 'success' | 'error';
  } | null>(null);
  const detailRequestId = useRef(0);
  const resourceRequestId = useRef(0);

  const notify = useCallback(
    (severity: 'success' | 'error', message: string) =>
      setNotification({ severity, message }),
    [],
  );

  const load = useCallback(
    async (showSpinner = true) => {
      if (showSpinner) setLoading(true);
      setError(null);
      try {
        setData(await api.applications());
      } catch (loadError) {
        setError(
          loadError instanceof Error ? loadError.message : String(loadError),
        );
      } finally {
        if (showSpinner) setLoading(false);
      }
    },
    [api],
  );

  useEffect(() => {
    load();
    const interval = window.setInterval(() => load(false), 30_000);
    return () => window.clearInterval(interval);
  }, [load]);

  const toggleFavorite = useCallback((application: ArgoApplication) => {
    setFavorites(current => {
      const next = new Set(current);
      const key = appKey(application);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      window.localStorage.setItem(FAVORITES_KEY, JSON.stringify([...next]));
      return next;
    });
  }, []);

  const changeView = (value: 'tiles' | 'table') => {
    setView(value);
    window.localStorage.setItem(VIEW_KEY, value);
  };

  const openApplication = useCallback(
    async (application: ArgoApplication, keepTab = false) => {
      const requestId = ++detailRequestId.current;
      setSelected(application);
      setPendingApp('');
      if (!keepTab) setAppTab('summary');
      setDetails(null);
      setSelectedResource(null);
      setResourceDetails(null);
      setDetailsLoading(true);
      try {
        const applicationDetails = await api.application(application);
        if (requestId === detailRequestId.current) {
          setDetails(applicationDetails);
        }
      } catch (detailError) {
        if (requestId === detailRequestId.current) {
          notify(
            'error',
            detailError instanceof Error
              ? detailError.message
              : String(detailError),
          );
        }
      } finally {
        if (requestId === detailRequestId.current) {
          setDetailsLoading(false);
        }
      }
    },
    [api, notify],
  );

  // ── the URL is the address of this view ──────────────────────────────────
  const urlState = useMemo<ArgoUrlState>(
    () => ({
      tab: pageTab,
      q: search,
      instance: instanceFilter,
      sync: syncFilter,
      health: healthFilter,
      project: projectFilter,
      cluster: clusterFilter,
      ns: namespaceFilter,
      autosync: autoSyncFilter,
      labels: labelFilter,
      favoritesOnly,
      view,
      page,
      app: selected
        ? urlAppKey(
            selected.instance,
            selected.appNamespace ?? '',
            selected.name,
          )
        : pendingApp,
      appTab,
      res: selectedResource
        ? urlResourceKey(selectedResource)
        : selected || pendingApp
        ? pendingRes
        : '',
      resTab,
    }),
    [
      pageTab,
      search,
      instanceFilter,
      syncFilter,
      healthFilter,
      projectFilter,
      clusterFilter,
      namespaceFilter,
      autoSyncFilter,
      labelFilter,
      favoritesOnly,
      view,
      page,
      selected,
      pendingApp,
      appTab,
      selectedResource,
      pendingRes,
      resTab,
    ],
  );

  const adoptUrl = useCallback((next: ArgoUrlState) => {
    setPageTab(next.tab);
    setSearch(next.q);
    setInstanceFilter(next.instance);
    setSyncFilter(next.sync);
    setHealthFilter(next.health);
    setProjectFilter(next.project);
    setClusterFilter(next.cluster);
    setNamespaceFilter(next.ns);
    setAutoSyncFilter(next.autosync);
    setLabelFilter(next.labels);
    setFavoritesOnly(next.favoritesOnly);
    setView(next.view);
    setPage(next.page);
    setAppTab(next.appTab);
    setResTab(next.resTab);
    // Applications and resources are named by key; the effects below resolve
    // them against loaded data, which is also how a pasted link opens one.
    setPendingApp(next.app);
    setPendingRes(next.res);
    if (!next.app) {
      setSelected(null);
      setDetails(null);
    }
    if (!next.res) {
      setSelectedResource(null);
      setResourceDetails(null);
    }
  }, []);

  const { copyShareUrl } = useUrlState<ArgoUrlState>({
    state: urlState,
    parse: parseArgoUrl,
    serialize: serializeArgoUrl,
    adopt: adoptUrl,
    navigationKeys: ARGO_NAVIGATION_KEYS,
  });

  const copyLink = useCallback(
    () => copyShareUrl(message => notify('success', message)),
    [copyShareUrl, notify],
  );

  const openResource = useCallback(
    async (resource: ArgoResource, keepTab = false) => {
      if (!selected) return;
      const requestId = ++resourceRequestId.current;
      setSelectedResource(resource);
      setPendingRes('');
      if (!keepTab) setResTab('summary');
      setResourceDetails(null);
      setResourceError(null);
      setResourceLoading(true);
      try {
        const loaded = await api.resource(selected, resource);
        if (requestId === resourceRequestId.current) {
          setResourceDetails(loaded);
        }
      } catch (resourceLoadError) {
        if (requestId === resourceRequestId.current) {
          setResourceError(
            resourceLoadError instanceof Error
              ? resourceLoadError.message
              : String(resourceLoadError),
          );
        }
      } finally {
        if (requestId === resourceRequestId.current) {
          setResourceLoading(false);
        }
      }
    },
    [api, selected],
  );

  const refreshApplication = useCallback(
    async (application: ArgoApplication, type: 'normal' | 'hard' = 'normal') => {
      const key = appKey(application);
      const refreshesSelected =
        selected?.instance === application.instance &&
        selected.name === application.name;
      const requestId = refreshesSelected
        ? ++detailRequestId.current
        : detailRequestId.current;
      setBusyKey(key);
      try {
        const refreshed = await api.refresh(application, type);
        if (refreshesSelected && requestId === detailRequestId.current) {
          const refreshedDetails = await api.application(refreshed);
          if (requestId !== detailRequestId.current) return;
          setSelected(refreshed);
          setDetails(refreshedDetails);
        }
        notify(
          'success',
          `${type === 'hard' ? 'Hard refresh' : 'Refresh'} requested for ${
            application.name
          }`,
        );
        await load(false);
      } catch (refreshError) {
        notify(
          'error',
          refreshError instanceof Error
            ? refreshError.message
            : String(refreshError),
        );
      } finally {
        setBusyKey(null);
      }
    },
    [api, load, notify, selected],
  );

  const requestSync = useCallback(
    async (application: ArgoApplication) => {
      const selectedDetails =
        details?.instance === application.instance &&
        details.name === application.name
          ? details
          : null;
      const key = appKey(application);
      setBusyKey(key);
      try {
        const access =
          selectedDetails?.access ?? (await api.access(application));
        if (!access.rbacCanSync) {
          notify(
            'error',
            `Your Argo CD RBAC policy does not allow ${application.name} to sync`,
          );
          return;
        }
        if (!access.syncWindowCanSync) {
          notify(
            'error',
            `The active Argo CD sync window blocks ${application.name}`,
          );
          return;
        }
        const resources =
          selectedDetails?.resources ??
          (await api.application(application)).resources;
        setSyncPanel({ application, resources });
      } catch (accessError) {
        notify(
          'error',
          accessError instanceof Error
            ? accessError.message
            : String(accessError),
        );
      } finally {
        setBusyKey(null);
      }
    },
    [api, details, notify],
  );

  const submitSync = useCallback(
    async (request: ArgoSyncRequest) => {
      if (!syncPanel) return;
      const { application } = syncPanel;
      const key = appKey(application);
      setBusyKey(key);
      try {
        await api.sync(application, request);
        notify(
          'success',
          request.dryRun
            ? `Dry-run sync started for ${application.name}`
            : `Sync started for ${application.name}`,
        );
        setSyncPanel(null);
        window.setTimeout(() => load(false), 1500);
        if (
          selected?.instance === application.instance &&
          selected.name === application.name
        ) {
          window.setTimeout(() => openApplication(application), 1500);
        }
      } catch (syncError) {
        notify(
          'error',
          syncError instanceof Error ? syncError.message : String(syncError),
        );
      } finally {
        setBusyKey(null);
      }
    },
    [api, load, notify, openApplication, selected, syncPanel],
  );

  const submitRollback = useCallback(async () => {
    if (!rollbackCandidate) return;
    const { application, id } = rollbackCandidate;
    const key = appKey(application);
    setBusyKey(key);
    setRollbackCandidate(null);
    try {
      await api.rollback(application, id, rollbackPrune);
      notify('success', `Rollback to deployment ${id} started`);
      window.setTimeout(() => {
        load(false);
        openApplication(application);
      }, 1500);
    } catch (rollbackError) {
      notify(
        'error',
        rollbackError instanceof Error
          ? rollbackError.message
          : String(rollbackError),
      );
    } finally {
      setBusyKey(null);
    }
  }, [api, load, notify, openApplication, rollbackCandidate, rollbackPrune]);

  const submitTerminate = useCallback(async () => {
    if (!selected) return;
    const key = appKey(selected);
    setBusyKey(key);
    setTerminateOpen(false);
    try {
      await api.terminateOperation(selected);
      notify('success', `Operation terminated for ${selected.name}`);
      window.setTimeout(() => openApplication(selected), 1000);
    } catch (terminateError) {
      notify(
        'error',
        terminateError instanceof Error
          ? terminateError.message
          : String(terminateError),
      );
    } finally {
      setBusyKey(null);
    }
  }, [api, notify, openApplication, selected]);

  const applications = data?.applications ?? [];
  const healthStatuses = useMemo(
    () =>
      [
        ...new Set(applications.map(application => application.healthStatus)),
      ].sort(),
    [applications],
  );
  const syncStatuses = useMemo(
    () =>
      [
        ...new Set(applications.map(application => application.syncStatus)),
      ].sort(),
    [applications],
  );
  const projects = useMemo(
    () =>
      [...new Set(applications.map(application => application.project))].sort(),
    [applications],
  );
  const clusters = useMemo(
    () =>
      [
        ...new Set(
          applications
            .map(
              application =>
                application.destinationName ?? application.destinationServer,
            )
            .filter((value): value is string => Boolean(value)),
        ),
      ].sort(),
    [applications],
  );
  const namespaces = useMemo(
    () =>
      [
        ...new Set(
          applications
            .map(application => application.destinationNamespace)
            .filter((value): value is string => Boolean(value)),
        ),
      ].sort(),
    [applications],
  );
  const labelOptions = useMemo(
    () =>
      [
        ...new Set(
          applications.flatMap(application =>
            Object.entries(application.labels).map(
              ([key, value]) => `${key}=${value}`,
            ),
          ),
        ),
      ].sort(),
    [applications],
  );

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return applications.filter(application => {
      if (favoritesOnly && !favorites.has(appKey(application))) return false;
      if (instanceFilter !== 'all' && application.instance !== instanceFilter)
        return false;
      if (syncFilter !== 'all' && application.syncStatus !== syncFilter)
        return false;
      if (healthFilter !== 'all' && application.healthStatus !== healthFilter)
        return false;
      if (projectFilter !== 'all' && application.project !== projectFilter)
        return false;
      if (
        clusterFilter !== 'all' &&
        (application.destinationName ?? application.destinationServer) !==
          clusterFilter
      )
        return false;
      if (
        namespaceFilter !== 'all' &&
        application.destinationNamespace !== namespaceFilter
      )
        return false;
      if (autoSyncFilter === 'enabled' && !application.automated) return false;
      if (autoSyncFilter === 'disabled' && application.automated) return false;
      if (
        labelFilter.length &&
        !labelFilter.every(label => {
          const [key, ...rest] = label.split('=');
          return application.labels[key] === rest.join('=');
        })
      )
        return false;
      if (!query) return true;
      return [
        application.name,
        application.project,
        application.repoUrl,
        application.path,
        application.destinationNamespace,
        ...Object.entries(application.labels).flat(),
      ].some(value => value?.toLowerCase().includes(query));
    });
  }, [
    applications,
    autoSyncFilter,
    clusterFilter,
    favorites,
    favoritesOnly,
    healthFilter,
    instanceFilter,
    labelFilter,
    namespaceFilter,
    projectFilter,
    search,
    syncFilter,
  ]);

  useEffect(() => {
    setPage(1);
  }, [
    search,
    instanceFilter,
    syncFilter,
    healthFilter,
    projectFilter,
    clusterFilter,
    namespaceFilter,
    autoSyncFilter,
    labelFilter,
    favoritesOnly,
  ]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const visible = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // A link naming an application opens it as soon as the list has loaded.
  useEffect(() => {
    if (!pendingApp || !data) return;
    const target = parseUrlAppKey(pendingApp);
    if (!target) {
      setPendingApp('');
      return;
    }
    const match = applications.find(
      application =>
        application.instance === target.instance &&
        application.name === target.name &&
        (application.appNamespace ?? '') === target.namespace,
    );
    if (match) {
      // Following a link: keep the tab the link named.
      openApplication(match, true);
    } else {
      setPendingApp('');
      notify('error', `Application ${target.name} is no longer available`);
    }
  }, [pendingApp, data, applications, openApplication, notify]);

  // …and its resource once that application's details arrive.
  useEffect(() => {
    if (!pendingRes || !details) return;
    const match = (details.resources ?? []).find(
      resource => urlResourceKey(resource) === pendingRes,
    );
    if (match) {
      openResource(match, true);
    } else {
      setPendingRes('');
    }
  }, [pendingRes, details, openResource]);

  useEffect(() => {
    setPage(currentPage => Math.min(currentPage, pageCount));
  }, [pageCount]);

  const counts = {
    healthy: applications.filter(
      application => application.healthStatus === 'Healthy',
    ).length,
    degraded: applications.filter(application =>
      ['Degraded', 'Missing'].includes(application.healthStatus),
    ).length,
    synced: applications.filter(
      application => application.syncStatus === 'Synced',
    ).length,
    outOfSync: applications.filter(
      application => application.syncStatus === 'OutOfSync',
    ).length,
  };

  const selectSx = { minWidth: 150 };

  return (
    <Page themeId="tool">
      <Header
        title="Argo CD"
        subtitle="Native GitOps operations across configured Argo CD instances"
      />
      <Content>
        <Tabs
          value={pageTab}
          onChange={(_event, value) => setPageTab(value)}
          sx={{ mb: 2, borderBottom: `1px solid ${COLORS.border}` }}
        >
          <Tab label="Applications" />
          <Tab label="ApplicationSets" />
        </Tabs>

        {pageTab === 'appsets' && <AppSetsView api={api} />}

        {pageTab === 'apps' && (
          <>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              sx={{ mb: 2 }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{ color: COLORS.text, fontSize: 13, fontWeight: 700 }}
                >
                  Applications
                </Typography>
                <Typography sx={{ color: COLORS.muted, fontSize: 12 }}>
                  Signed in to Argo CD as{' '}
                  {data?.user.email ?? 'Microsoft Entra user'}
                </Typography>
              </Box>
              <Stack
                direction="row"
                spacing={1}
                sx={{ alignSelf: { xs: 'stretch', md: 'center' } }}
              >
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={view}
                  onChange={(_event, value) => value && changeView(value)}
                >
                  <ToggleButton value="tiles" aria-label="Tiles view">
                    <ViewModuleIcon sx={{ fontSize: 18 }} />
                  </ToggleButton>
                  <ToggleButton value="table" aria-label="Table view">
                    <ViewListIcon sx={{ fontSize: 18 }} />
                  </ToggleButton>
                </ToggleButtonGroup>
                <Tooltip title="Show favorites only">
                  <ToggleButton
                    size="small"
                    value="favorites"
                    selected={favoritesOnly}
                    onChange={() => setFavoritesOnly(current => !current)}
                  >
                    <StarIcon
                      sx={{
                        fontSize: 18,
                        color: favoritesOnly ? COLORS.amber : undefined,
                      }}
                    />
                  </ToggleButton>
                </Tooltip>
                <Button
                  variant="outlined"
                  startIcon={
                    loading ? <CircularProgress size={14} /> : <RefreshIcon />
                  }
                  disabled={loading}
                  onClick={() => load()}
                  sx={{ textTransform: 'none' }}
                >
                  Refresh all
                </Button>
                <Tooltip title="Copy a link to exactly this view">
                  <Button
                    variant="outlined"
                    startIcon={<LinkIcon />}
                    onClick={copyLink}
                    sx={{ textTransform: 'none' }}
                  >
                    Copy link
                  </Button>
                </Tooltip>
              </Stack>
            </Stack>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {data?.instances.some(instance => !instance.connected) && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {data.instances
                  .filter(instance => !instance.connected)
                  .map(instance => `${instance.name}: ${instance.error}`)
                  .join(' · ')}
              </Alert>
            )}

            <Stack
              direction="row"
              spacing={1.5}
              sx={{ mb: 2, overflowX: 'auto', pb: 0.5 }}
            >
              <Metric
                label="Applications"
                value={applications.length}
                color={COLORS.blue}
              />
              <Metric
                label="Healthy"
                value={counts.healthy}
                color={COLORS.green}
              />
              <Metric
                label="Degraded"
                value={counts.degraded}
                color={COLORS.red}
              />
              <Metric
                label="Synced"
                value={counts.synced}
                color={COLORS.green}
              />
              <Metric
                label="Out of sync"
                value={counts.outOfSync}
                color={COLORS.amber}
              />
            </Stack>

            <HealthBar applications={applications} onSelect={setHealthFilter} />

            <Box
              sx={{
                mb: 2,
                p: 1.5,
                border: `1px solid ${COLORS.border}`,
                borderRadius: 2,
                bgcolor: COLORS.surface,
              }}
            >
              <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1.5 }}>
                <TextField
                  size="small"
                  value={search}
                  onChange={event => setSearch(event.target.value)}
                  placeholder="Search applications, projects, repositories, labels..."
                  sx={{ flex: 1, minWidth: 240 }}
                  InputProps={{
                    startAdornment: (
                      <SearchIcon sx={{ mr: 1, color: COLORS.muted }} />
                    ),
                  }}
                />
                <TextField
                  select
                  size="small"
                  label="Instance"
                  value={instanceFilter}
                  onChange={event => setInstanceFilter(event.target.value)}
                  sx={selectSx}
                >
                  <MenuItem value="all">All instances</MenuItem>
                  {(data?.instances ?? []).map(instance => (
                    <MenuItem key={instance.name} value={instance.name}>
                      {instance.name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Project"
                  value={projectFilter}
                  onChange={event => setProjectFilter(event.target.value)}
                  sx={selectSx}
                >
                  <MenuItem value="all">All projects</MenuItem>
                  {projects.map(project => (
                    <MenuItem key={project} value={project}>
                      {project}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Cluster"
                  value={clusterFilter}
                  onChange={event => setClusterFilter(event.target.value)}
                  sx={selectSx}
                >
                  <MenuItem value="all">All clusters</MenuItem>
                  {clusters.map(cluster => (
                    <MenuItem key={cluster} value={cluster}>
                      {cluster}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Namespace"
                  value={namespaceFilter}
                  onChange={event => setNamespaceFilter(event.target.value)}
                  sx={selectSx}
                >
                  <MenuItem value="all">All namespaces</MenuItem>
                  {namespaces.map(namespace => (
                    <MenuItem key={namespace} value={namespace}>
                      {namespace}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Sync status"
                  value={syncFilter}
                  onChange={event => setSyncFilter(event.target.value)}
                  sx={selectSx}
                >
                  <MenuItem value="all">All sync states</MenuItem>
                  {syncStatuses.map(status => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Health"
                  value={healthFilter}
                  onChange={event => setHealthFilter(event.target.value)}
                  sx={selectSx}
                >
                  <MenuItem value="all">All health states</MenuItem>
                  {healthStatuses.map(status => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Auto-sync"
                  value={autoSyncFilter}
                  onChange={event => setAutoSyncFilter(event.target.value)}
                  sx={selectSx}
                >
                  <MenuItem value="all">Any</MenuItem>
                  <MenuItem value="enabled">Enabled</MenuItem>
                  <MenuItem value="disabled">Disabled</MenuItem>
                </TextField>
                <Autocomplete
                  multiple
                  size="small"
                  options={labelOptions}
                  value={labelFilter}
                  onChange={(_event, value) => setLabelFilter(value)}
                  renderInput={params => (
                    <TextField {...params} size="small" label="Labels" />
                  )}
                  sx={{ minWidth: 220, flex: 1 }}
                />
              </Stack>
            </Box>

            {loading && !data ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {view === 'tiles' ? (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        md: 'repeat(2, minmax(0, 1fr))',
                        xl: 'repeat(3, minmax(0, 1fr))',
                      },
                      gap: 2,
                    }}
                  >
                    {visible.map(application => {
                      const key = appKey(application);
                      return (
                        <ApplicationCard
                          key={`${key}/${application.appNamespace ?? ''}`}
                          application={application}
                          busy={busyKey === key}
                          favorite={favorites.has(key)}
                          onToggleFavorite={() => toggleFavorite(application)}
                          onOpen={() => openApplication(application)}
                          onRefresh={() => refreshApplication(application)}
                          onSync={() => requestSync(application)}
                        />
                      );
                    })}
                  </Box>
                ) : (
                  <ApplicationsTable
                    applications={visible}
                    busyKey={busyKey}
                    favorites={favorites}
                    onToggleFavorite={toggleFavorite}
                    onOpen={openApplication}
                    onRefresh={refreshApplication}
                    onSync={requestSync}
                  />
                )}

                {filtered.length === 0 && (
                  <Box sx={{ p: 6, textAlign: 'center' }}>
                    <ErrorOutlineIcon
                      sx={{ color: COLORS.muted, fontSize: 38 }}
                    />
                    <Typography
                      sx={{ mt: 1, color: COLORS.text, fontWeight: 700 }}
                    >
                      No applications match these filters
                    </Typography>
                  </Box>
                )}

                {pageCount > 1 && (
                  <Box
                    sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}
                  >
                    <Pagination
                      count={pageCount}
                      page={page}
                      onChange={(_event, value) => setPage(value)}
                      color="primary"
                    />
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </Content>

      <ApplicationDrawer
        api={api}
        application={selected}
        details={details}
        loading={detailsLoading}
        busy={Boolean(selected) && busyKey === appKey(selected!)}
        tab={appTab}
        onTabChange={setAppTab}
        onClose={() => {
          detailRequestId.current += 1;
          resourceRequestId.current += 1;
          setSelected(null);
          setPendingApp('');
          setAppTab('summary');
          setDetails(null);
          setDetailsLoading(false);
          setSelectedResource(null);
          setPendingRes('');
          setResourceDetails(null);
          setResourceError(null);
          setResourceLoading(false);
        }}
        onRefresh={type => selected && refreshApplication(selected, type)}
        onSync={() => selected && requestSync(selected)}
        onResource={openResource}
        onRollback={entry => {
          if (selected && entry.id !== undefined) {
            setRollbackPrune(false);
            setRollbackCandidate({
              application: selected,
              id: entry.id,
              revision: entry.revision,
            });
          }
        }}
        onTerminate={() => setTerminateOpen(true)}
      />

      <ResourceDialog
        api={api}
        application={selected}
        resource={selectedResource}
        details={resourceDetails}
        loading={resourceLoading}
        error={resourceError}
        tab={resTab}
        onTabChange={setResTab}
        onClose={() => {
          resourceRequestId.current += 1;
          setSelectedResource(null);
          setPendingRes('');
          setResTab('summary');
          setResourceDetails(null);
          setResourceError(null);
          setResourceLoading(false);
        }}
        onNotify={notify}
        onChanged={() => selected && openApplication(selected)}
      />

      <SyncPanel
        application={syncPanel?.application ?? null}
        resources={syncPanel?.resources ?? []}
        busy={
          Boolean(syncPanel) && busyKey === appKey(syncPanel!.application)
        }
        onClose={() => setSyncPanel(null)}
        onSubmit={submitSync}
      />

      <Dialog
        open={Boolean(rollbackCandidate)}
        onClose={() => setRollbackCandidate(null)}
      >
        <DialogTitle>Roll back application?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Roll back <strong>{rollbackCandidate?.application.name}</strong> to
            deployment <strong>{rollbackCandidate?.id}</strong>
            {rollbackCandidate?.revision
              ? ` (${rollbackCandidate.revision.slice(0, 8)})`
              : ''}
            ? The application will drift from Git until the next sync.
          </DialogContentText>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Checkbox
                size="small"
                checked={rollbackPrune}
                onChange={event => setRollbackPrune(event.target.checked)}
              />
            }
            label="Prune resources not in the target revision"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRollbackCandidate(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<ReplayIcon />}
            onClick={submitRollback}
          >
            Roll back
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={terminateOpen} onClose={() => setTerminateOpen(false)}>
        <DialogTitle>Terminate operation?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Stop the running operation on <strong>{selected?.name}</strong>?
            Partially applied resources are left as-is.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTerminateOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={submitTerminate}>
            Terminate
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(notification)}
        autoHideDuration={5000}
        onClose={() => setNotification(null)}
      >
        {notification ? (
          <Alert
            severity={notification.severity}
            onClose={() => setNotification(null)}
          >
            {notification.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Page>
  );
}
